/**
 * Answers the API gave for short addresses that the loaded numbers could not resolve (a cold deep link, a record made in
 * the last minute). Kept per project; cleared when the active project changes. `error` is remembered for the session
 * too, so a failing endpoint is asked once per address and the page then shows its own "not found" instead of a spinner.
 */
import type { ResolvedCandidate } from '../services/recordRefResolver';

export type ResolutionEntry =
  | { status: 'found'; id: string }
  | { status: 'notFound' }
  | { status: 'error' }
  | { status: 'ambiguous'; candidates: ResolvedCandidate[] };

let project: string | null = null;
const entries = new Map<string, ResolutionEntry>();
const inflight = new Set<string>();
const listeners = new Set<() => void>();

const key = (segment: string) => segment.toUpperCase();

function scope(projectId: string): void {
  if (project !== projectId) { project = projectId; entries.clear(); inflight.clear(); }
}

export function getResolution(projectId: string | null, segment: string): ResolutionEntry | undefined {
  return projectId && project === projectId ? entries.get(key(segment)) : undefined;
}

/** Segment (upper case) -> uuid for everything resolved in this project. */
export function resolvedIds(projectId: string | null): Map<string, string> {
  const out = new Map<string, string>();
  if (!projectId || project !== projectId) return out;
  for (const [seg, e] of entries) if (e.status === 'found') out.set(seg, e.id);
  return out;
}

export function setResolution(projectId: string, segment: string, entry: ResolutionEntry): void {
  scope(projectId);
  entries.set(key(segment), entry);
  inflight.delete(key(segment));
  listeners.forEach(l => l());
}

/** True the first time a segment is claimed for fetching in this project, false while it is already being fetched or answered. */
export function claimResolution(projectId: string, segment: string): boolean {
  scope(projectId);
  const k = key(segment);
  if (entries.has(k) || inflight.has(k)) return false;
  inflight.add(k);
  return true;
}

export function subscribeResolution(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function resetResolutionForTests(): void {
  project = null; entries.clear(); inflight.clear(); listeners.clear();
}
